import { User } from "firebase/auth";
import { formatResult, getFormDifficulty, getRecords } from "./utils/utils";

export interface SystemMessage {
  message: string;
  id: string;
}

export interface ITeamStats { results: number, players: number, likes: number, comments: number, followers: number, requests: number, lastUpdated: Date, wins?: number, draws?: number, losses?: number, winRate?: number, xpg?: number, goalsFor?: number, goalsAgainst?: number, goalDiff?: number };

export interface AuthPayload {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
};

export type ProfilePhotoSlot = 'profilePhoto' | 'profilePhotoAway' | 'profilePhotoHomePotm' | 'profilePhotoAwayPotm';

export interface IPlayerRecord {
  number: number;
  img?: string;
  profilePhoto?: string;
  profilePhotoAway?: string;
  profilePhotoHomePotm?: string;
  profilePhotoAwayPotm?: string;
  dateOfBirth?: string;
}

export interface IPlayerNote {
  content: string;
  date: string;
  author?: string;
  ranks: Record<string, number>;
}

export interface IOptions {
  /* App related */
  hideForm?: boolean;
  /** When set, the Form tab is only visible to staff (owner/coach/manager/admin) and supporters with the Form Viewer role. */
  formViewersOnly?: boolean;
  hideTeam?: boolean;
  hideFixtures?: boolean;
  hideInfo?: boolean;
  hideBlog?: boolean;
  hideStats?: boolean;
  hideMatchTools?: boolean;
  hideInsights?: boolean;
  hideAI?: boolean;
  hideTraining?: boolean;
  hidePlayerSpotlight?: boolean;
  hideCoachNotes?: boolean;

  /* Stats related */
  hideScorers?: boolean;
  hideAssists?: boolean;
  hideCleanSheets?: boolean;
  hidePlayerInfluence?: boolean;

  /* Report related */
  hideGameRating?: boolean;
  hideMatchReport?: boolean;
  hidePlayedAvatars?: boolean;
  hideSocialLinks?: boolean;
  hideReportComments?: boolean;
  hideReportLikes?: boolean;
  hidePlayerOfMatch?: boolean;
  hideCaptain?: boolean;

  /* Team related */
  hideTeamPhoto?: boolean;
  hideHonours?: boolean;
  hideTeamLogo?: boolean;

  /* Fixture Related */
  hideAttending?: boolean;
}

export interface IUser {
  uid: string;
  displayName: string;
  email: string;
  photoURL?: string;
  isScout?: boolean;
  createdAt: string;
  followIds?: string[];
  requestIds?: string[];
  followClubIds?: string[];
  requestClubIds?: string[];
  favouriteIds?: string[];
  ticketIds?: string[];
}

export interface IControl extends TeamConfig {
  options?: IOptions;
  homeVenue?: string;
  primaryColor: string;
  resultRownthchild: string;
  leagueDetails: string;
  rules: string[];
  fixtureDetails: string;
  homeDirections: string;
  homeDirectionsUrl: string;
  homeMapPhoto: string;
  spectatorDetails: string;
  kitDetails: string;
  teamPhoto: string;
  teamNickname: string;
  teamName: string;
  players: Record<string, IPlayerRecord>;
  sisterTeams: string[];
  /** Display-only names used exclusively on this team's Form tab. */
  formTeamAliases?: { teamName: string; alias: string }[];
  /** Division names in rank order (strongest first), used to seed/rank teams on the Form tab. */
  divisionRankings?: string[];
  /**
   * Per-result division tags for results that aren't our own editable ResultInstance
   * (other-result text lines and followed-team results). Keyed by a stable result key.
   */
  formResultDivisions?: Record<string, string>;
  /**
   * Keys of other-result lines marked as friendlies. Friendlies are excluded from
   * division analysis and progression plotting, since divisions can differ in friendlies.
   */
  formResultFriendlies?: string[];
  /** UUIDs of ClubRoots teams in the same club to auto-track on the Form tab */
  linkedSisterTeamUuids?: string[];
  honours: string;
  instagramUrl?: string;
  veoClubhouseUrl?: string;
  staticUrl?: string;
  shownWelcome?: boolean;
  shownSettings?: boolean;
  shownFixtures?: boolean;
  weeklyAwards?: string[];
  meetTimeOffset?: number;
  inviteCode?: string;
  /** Enables the auto-created "Parents' Player of the Week" poll flow on results. */
  parentPlayerOfWeekEnabled?: boolean;
  fixtureAttendanceReminder?: IDiaryAttendanceReminderDefaults;
  trainingAttendanceReminder?: IDiaryAttendanceReminderDefaults & {
    requestEachTraining: boolean;
  };
}

export interface TeamConfig {
  name: string;
  owner: string;
  ownerUid: string;
  email: string;
  uuid: string;
  league: string;
  division: string;
  fullTimeUrl?: string;
  fullTimeTeamName?: string;
  badge: string;
  town: string
  county: string;
  country: string;
  followApprovalDisabled?: boolean;
  clubId?: string;
  staticUrl?: string;
  createdAt?: string;
}

export interface ClubConfig {
  uuid: string;
  name: string;
  ownerUid: string;
  ownerName?: string;
  ownerEmail?: string;
  badge?: string;
  town?: string;
  county?: string;
  country?: string;
  primaryColor?: string;
  staticUrl?: string;
}

export interface IClubControl extends ClubConfig {
  teamIds: string[];
  instagramUrl?: string;
  shownWelcome?: boolean;
}

export interface IClubPitch {
  id: string;
  defaultName: string;
  alias?: string;
  pitchFormat?: string;
  surfaceType?: string;
  pausedWeekendStarts?: string[]; // YYYY-MM-DD (Saturday)
  createdAt: string;
  createdByUid?: string;
}

export interface IClubPitchBooking {
  id: string;
  pitchId: string;
  teamId: string;
  teamName: string;
  teamOwnerUid: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  bookedByUid: string;
  bookedByName: string;
  createdAt: string;
  updatedAt?: string;
}

/** A single time range within a pitch availability slot */
export interface IPitchSlotTimeRange {
  startTime: string; // HH:mm
  endTime: string; // HH:mm
}

/**
 * Defines the recurring available booking slots for a pitch.
 * If a pitch has one or more slots, coaches can only book within those defined windows.
 */
export interface IClubPitchSlot {
  id: string;
  pitchId: string;
  /** Days of week this slot applies to: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat */
  daysOfWeek: number[];
  timeRanges: IPitchSlotTimeRange[];
  createdAt: string;
  createdByUid?: string;
}

export interface IFixturePresser {
  opponent: string;
  oppositionCrestUrl: string;
  tagline: string;
  venue: string;
  meet: string;
  kickOff: string;
  parking: string;
  kit: string;
  squad: string;
  opposition: string;
  lastWeek: string;
  coachMessage: string;
  focus: string;
}

export interface IFixture {
  /** Stable identity for reminders; legacy fixtures are backfilled before scheduling is enabled. */
  id?: string;
  date: string;
  venue: string;
  time: string;
  url: string;
  teams: string[];
  attending?: string[];
  notAttending?: string[];
  isHome?: boolean;
  matchType?: MatchType;
  matchFormat?: MatchFormat;
  surface?: PitchSurface; // e.g., "Grass", "3G/Astro", "Indoor"
  notes?: string;
  inviteOnly?: boolean;
  uploadedImageUrl?: string;
  spotlightPlayer?: string;
  presser?: IFixturePresser;
  attendanceReminder?: Partial<IDiaryAttendanceReminderDefaults>;
  /** Total session fee in pence (e.g. 500 = £5.00). Divided among selected players when generating an invoice. */
  fee?: number;
}

export interface TimeSlot {
  /** Stable identity for slot-specific attendance and reminders. */
  id?: string;
  day: number;
  startTime: string;
  endTime: string;
}

export interface ITrainingSchedule {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  location: string;
  slots: TimeSlot[];
  attending?: string[];
  notAttending?: string[];
  attendanceOverrides: Record<string, Record<string, boolean>>; // Legacy date -> supporter UID -> attending
  attendanceOverridesBySlot?: Record<string, Record<string, Record<string, boolean>>>;
  attendanceReminder?: Partial<IDiaryAttendanceReminderDefaults> & {
    requestEachTraining?: boolean;
  };
}

/** Legacy results store a single name; shared awards store every winner. */
export type AwardWinners = string | string[];

export interface IResult {
  captain?: string;
  playerOfTheMatch?: string;
  report: string;
  score: string;
  venue: string;
  team: string;
  result: string;
  date: string;
  videoLink?: string;
  scorers: string[];
  assists: string[];
  played?: string[];
  cleanSheet?: string[];
  goalKeepers?: string[];
  defenders?: string[];
  midfielders?: string[];
  attackers?: string[];
  otherLinks?: string[];
  likes?: string[];
  comments?: Record<string, string>;
  links?: string[];
  matchType?: MatchType; // e.g., "league", "friendly", "cup"
  matchFormat?: MatchFormat; // e.g., "11-a-side", "7-a-side", "5-a-side"
  surface?: PitchSurface; // e.g., "Grass", "3G/Astro", "Indoor"
  weather?: WeatherCondition[];
  formation?: string; // e.g., "4-4-2", "3-5-2", "4-3-3"
  /** Division the opponent was playing in for this match, e.g. "Division 2". */
  division?: string;
  /** Award name -> winner. An array when an award is shared, e.g. a tied parent vote. */
  otherAwards?: Record<string, AwardWinners>;
}

export interface ISupporterRecord {
  supporterName: string;
  uid: string;
  email?: string;
  lastUpdated: string;
  player?: string | string[];
  primaryForPlayer?: boolean;  // primary contact for linked player; used to receive payment requests
  manager?: boolean;
  coach?: boolean;
  /** Can view the Form tab when it is restricted to staff (options.formViewersOnly). */
  formViewer?: boolean;
  clubAdmin?: boolean;  // club-level admin — can administer the club and all its teams
  mutedInChat?: boolean;
  removedFromChat?: boolean;
}

export interface IPlayerStats {
  braces: number;
  hatricks: number;
  cleanSheets?: number;
  games: number;
  assists?: number;
  goals: number;
  /** Votes received in settled parent polls during the current season. */
  parentVotes?: number;
  /** Poll contributions retained to make settlement retries idempotent. */
  parentVotePolls?: Record<string, number>;
  profile?: string;
  attendance?: {
    training?: Record<string, IPlayerAttendanceEntry>;
    fixtures?: Record<string, IPlayerAttendanceEntry>;
  };
}

export type DiaryAttendanceEventType = "fixture" | "training";
export type DiaryAttendanceResponseStatus = "pending" | "attending" | "notAttending" | "maybe";
export type DiaryAttendanceDeliveryStatus = "queued" | "sent" | "sendFailed";
export type DiaryAttendanceRequestStatus = "scheduled" | "sending" | "sent" | "closed" | "cancelled" | "expired";
export type DiaryAttendanceReminderLeadHours = 6 | 12 | 24;
export type DiaryAttendanceNotificationKind = "attendanceRequest" | "upcomingReminder";

export interface IDiaryAttendanceReminderDefaults {
  leadHours: DiaryAttendanceReminderLeadHours;
  remindAccepted: boolean;
  requestAttendance?: boolean;
  requestLeadHours?: number;
  reminderLeadHours?: number;
}

export interface IDiaryAttendanceEventSnapshot {
  name: string;
  date: string;
  startTime: string;
  location: string;
}

export interface IDiaryAttendanceResponse {
  status: Exclude<DiaryAttendanceResponseStatus, "pending">;
  respondedAt: number;
  reason?: string;
  note?: string;
}

export interface IDiaryAttendanceRecipientState {
  uid: string;
  playerNames: string[];
  deliveryStatus: DiaryAttendanceDeliveryStatus;
  responseStatus: DiaryAttendanceResponseStatus;
  notificationKind: DiaryAttendanceNotificationKind;
  queuedAt: number;
  attemptCount: number;
  lastAttemptAt?: number;
  sentAt?: number;
  openedAt?: number;
  respondedAt?: number;
  chaseUpSentAt?: number;
  response?: IDiaryAttendanceResponse;
}

export interface IDiaryAttendanceRequest {
  requestId: string;
  teamUid: string;
  eventType: DiaryAttendanceEventType;
  eventId: string;
  slotId?: string;
  eventDate: string;
  eventStartAt: number;
  snapshot: IDiaryAttendanceEventSnapshot;
  status: DiaryAttendanceRequestStatus;
  createdAt: number;
  updatedAt: number;
  sentAt?: number;
  closedAt?: number;
  cancelledAt?: number;
  chaseUpSentAt?: number;
  recipients: Record<string, IDiaryAttendanceRecipientState>;
}

export interface IDiaryAttendanceRecipientProjection {
  requestId: string;
  teamUid: string;
  eventType: DiaryAttendanceEventType;
  eventId: string;
  slotId?: string;
  eventDate: string;
  eventStartAt: number;
  snapshot: IDiaryAttendanceEventSnapshot;
  deliveryStatus: DiaryAttendanceDeliveryStatus;
  responseStatus: DiaryAttendanceResponseStatus;
  notificationKind: DiaryAttendanceNotificationKind;
  requestStatus?: DiaryAttendanceRequestStatus;
  sentAt?: number;
  openedAt?: number;
  respondedAt?: number;
  response?: IDiaryAttendanceResponse;
}

export interface IPlayerAttendanceEntry {
  eventType: DiaryAttendanceEventType;
  eventId: string;
  slotId?: string;
  eventDate: string;
  eventName: string;
  status: Exclude<DiaryAttendanceResponseStatus, "pending"> | "unknown";
  requestId?: string;
  responderUid?: string;
  updatedAt: number;
  reason?: string;
}

// Read-only per-player season aggregation shown in the Player Journey view.
export interface IPlayerJourneyMatch {
  date: string;
  opponent: string;
  score: string;
  result: string;
  scored: number;
  assisted: number;
  potm: boolean;
  cleanSheet: boolean;
  otherAwards: string[];
}

export interface IPlayerJourneyKeyMoment {
  date: string;
  description: string;
}

export interface IPlayerJourney {
  playerName: string;
  appearances: number;
  goals: number;
  assists: number;
  potmCount: number;
  otherAwardsCount: number;
  attendancePercent: number | null;
  trainingAttendancePercent?: number | null;
  fixtureAttendancePercent?: number | null;
  combinedAttendancePercent?: number | null;
  attendanceReasons?: Array<{ date: string; eventName: string; reason: string }>;
  matches: IPlayerJourneyMatch[];
  keyMoments: IPlayerJourneyKeyMoment[];
  latestCoachNote: IPlayerNote | null;
  narrativeSummary: string;
}

// Coach-editable end-of-season report, optionally seeded from an AI insight.
export interface IPlayerSeasonReport {
  whatImproved: string;
  keyAchievements: string;
  coachSummary: string;
  nextSeasonFocus: string;
  generatedAt: string;
  aiInsightId?: string;
  editedByCoach?: boolean;
}

export interface IBlogComment {
  id: string;
  userId: string;
  author: string;
  text: string;
  date: string;
}

export interface IBlogPost {
  content: string;
  date: string;
  title: string;
  likes?: Record<string, boolean>;
  likeNames?: Record<string, string>;
  comments?: IBlogComment[];
}

export interface IPoll {
  id: string;
  title: string;
  description?: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  closesAt?: string;
  status: 'open' | 'closed';
  type: 'single' | 'multiple' | 'text' | 'rating';
  /** Identifies system-generated polls that need special handling (e.g. auto-award on close). */
  kind?: 'parentPlayerOfWeek';
  /** For kind === 'parentPlayerOfWeek': `${result.date}|${result.team}`, links the poll to a result. */
  linkedResultKey?: string;
  /** For kind === 'parentPlayerOfWeek': true once the winner has been written to the result's otherAwards. */
  awardApplied?: boolean;
  options: Record<string, {
    text: string;
    order: number;
  }>;
  votes: Record<string, {
    optionIds: string[];
    text?: string;
    rating?: number;
    votedAt: string;
    voterName?: string;
  }>;
  settings: {
    anonymous: boolean;
    allowMultiple: boolean;
    showResults: 'always' | 'after_vote' | 'after_close';
    notifyTeam: boolean;
    restrictToPlayers: boolean;
  };
  results?: {
    totalVotes: number;
    optionCounts: Record<string, number>;
    computedAt: string;
  };
}

export type PaymentStatus = 'pending' | 'paid' | 'overdue' | 'cancelled';
export type PaymentMethod = 'card' | 'cash' | 'bank_transfer' | 'other';
export type PaymentRequestType = 'match_fee' | 'training_fee' | 'tournament' | 'subscription' | 'kit' | 'custom';

export interface IPaymentRequest {
  id: string;
  title: string;
  description?: string;
  amount: number;           // in pence, e.g. 500 = £5.00
  currency: 'GBP' | 'EUR';
  dueDate?: string;         // yyyyMMdd
  createdBy: string;        // uid
  createdByName: string;
  createdAt: string;        // ISO
  status: 'open' | 'closed' | 'cancelled';
  recipientPlayerNames: string[];  // empty = all players; stored as player names
  extraPlayerNames?: string[];     // trialists / loans included in cost split but without system accounts
  linkedFixtureDate?: string; // yyyyMMdd — optional link to a fixture
  linkedFixtureKey?: string;  // precise fixture key (date|time|teams) to disambiguate same-date fixtures
  excludedFixtureKeys?: string[]; // keys of fixtures this request should NOT display on (used to dismiss legacy date-matched requests)
  type: PaymentRequestType;
  payUrl?: string;              // optional online banking / payment link (https only)
}

export interface ITransaction {
  id: string;
  requestId: string;
  uid: string;              // who paid
  displayName: string;
  amount: number;           // in pence
  status: PaymentStatus;
  method: PaymentMethod;
  paidAt?: string;          // ISO
  confirmedBy?: string;     // uid of manager (for cash)
  notes?: string;
}

// ---- Club Payment Setup ----
export interface IClubPaymentSetup {
  id: string;
  clubId: string;
  title: string;
  description?: string;
  backgroundImageUrl?: string;
  amount: number;           // in pence, e.g. 500 = £5.00
  currency: 'GBP' | 'EUR';
  type: 'annual_membership' | 'seasonal' | 'one_off' | 'custom';
  year?: number;            // e.g. 2025 for annual membership
  dueDate?: string;         // yyyyMMdd
  status: 'active' | 'closed';
  createdBy: string;        // uid
  createdByName: string;
  createdAt: string;        // ISO
  allowMultiple?: boolean;  // if true, members can make multiple payments for this setup
}

// ---- Club Member Payment Record ----
export interface IClubMemberPayment {
  id: string;
  paymentSetupId: string;
  clubId: string;
  uid: string;              // who paid
  email: string;
  displayName: string;
  amount: number;           // actual amount paid (may differ due to discount)
  currency: 'GBP' | 'EUR';
  status: 'paid' | 'pending';
  paidAt?: string;          // ISO
  method: 'mock' | 'card' | 'cash' | 'bank_transfer' | 'other';
  discountLinkId?: string;  // populated when paid via a discount link
  confirmedBy?: string;     // uid of admin (for cash confirmations)
  notes?: string;
  deleted?: boolean;        // soft-delete flag (payment still visible in profile history)
  refunded?: boolean;       // indicates if this payment was refunded
  refundedAt?: string;      // ISO timestamp of when refund was processed
  refundedBy?: string;      // uid of admin who refunded it
  quantity?: number;        // number of units paid (default 1, used when allowMultiple=true)
}

// ---- Discount Offer Link ----
export interface IClubDiscountLink {
  id: string;               // opaque UUID token used in the URL
  paymentSetupId: string;
  clubId: string;
  overrideAmount: number;   // discounted amount in pence
  currency: 'GBP' | 'EUR';
  offerEmail: string;       // lowercase — intended recipient
  createdBy: string;        // uid
  createdAt: string;        // ISO
  consumed: boolean;
  consumedAt?: string;      // ISO
  consumedBy?: string;      // uid
  useCount: number;
}

// ---- Unified payment history row (used in ProfileDialog) ----
export type PaymentHistorySource = 'team_request' | 'club_payment';

export interface IPaymentHistoryRow {
  id: string;
  source: PaymentHistorySource;
  title: string;
  amount: number;           // in pence
  currency: 'GBP' | 'EUR';
  status: 'paid' | 'pending' | 'overdue' | 'cancelled';
  paidAt?: string;
  teamOrClubName: string;
  createdAt: string;
  requestId?: string;       // team records only
  paymentSetupId?: string;  // club records only
  clubId?: string;          // club records only
}

// ---- Club Links ----
export interface IClubLink {
  id: string;
  text: string;
  url?: string;
  backgroundImageUrl?: string;
  order: number;
  group?: string;
  type?: 'link' | 'whatsapp';
  message?: string;
}

// ---- About Us ----
export type AboutPageTemplate = 'html' | 'documents' | 'directions' | 'contact';

export interface IAboutDocument {
  id: string;
  name: string;
  gsUrl: string;
  downloadUrl: string;
  uploadedAt: string;
  size?: number;
}

export interface IAboutPage {
  id: string;
  title: string;
  template: AboutPageTemplate;
  order: number;
  // html template
  htmlContent?: string;
  // documents template
  documents?: IAboutDocument[];
  // directions template
  address?: string;
  directionsHtml?: string;
  // contact template
  contactEmail?: string;
  contactPhone?: string;
  facebookUrl?: string;
  twitterUrl?: string;
  instagramUrl?: string;
  youtubeUrl?: string;
  websiteUrl?: string;
  whatsappNumber?: string;
}

export class ResultInstance implements IResult {
  constructor(result: IResult) {
    this.date = result.date;
    this.team = result.team;
    this.venue = result.venue;
    this.score = result.score;
    this.result = result.result;
    this.report = result.report;
    this.videoLink = result.videoLink;
    this.scorers = result.scorers;
    this.assists = result.assists;
    this.played = result.played;
    this.cleanSheet = result.cleanSheet;
    this._comments = result.comments;
    this._likes = result.likes;
    this.links = result.links;
    this.otherLinks = result.otherLinks;
    this.difficulty = 0;
    this.initialisedStars = false;
    this.goalKeepers = result.goalKeepers;
    this.defenders = result.defenders;
    this.midfielders = result.midfielders;
    this.attackers = result.attackers;
    this.matchType = result.matchType;
    this.matchFormat = result.matchFormat;
    this.surface = result.surface;
    this.weather = result.weather;
    this.formation = result.formation;
    this.captain = result.captain;
    this.playerOfTheMatch = result.playerOfTheMatch;
    this.otherAwards = result.otherAwards;
    this.division = result.division;
  }

  public get comments(): Record<string, string> | undefined {
    return this._comments;
  }

  public set comments(value: Record<string, string> | undefined) {
    this._comments = value;
  }
  public get likes(): string[] | undefined {
    return this._likes;
  }

  public set likes(value: string[] | undefined) {
    this._likes = value;
  }


  public toDbObject = (): IResult => {
    // we need to do this as we cannot store class instances directly in Firebase
    // Convert to array of IResult objects:
    const iResult: IResult = ({
      report: this.report,
      score: this.score,
      venue: this.venue,
      team: this.team,
      result: this.result,
      date: this.date,
      videoLink: this.videoLink,
      scorers: this.scorers,
      assists: this.assists,
      played: this.played,
      cleanSheet: this.cleanSheet,
      otherLinks: this.otherLinks,
      likes: this._likes,
      comments: this._comments,
      links: this.links,
      matchType: this.matchType,
      matchFormat: this.matchFormat,
      surface: this.surface,
      weather: this.weather,
      formation: this.formation,
      goalKeepers: this.goalKeepers,
      defenders: this.defenders,
      midfielders: this.midfielders,
      attackers: this.attackers,
      captain: this.captain,
      playerOfTheMatch: this.playerOfTheMatch,
      otherAwards: this.otherAwards,
      division: this.division,

    });
    const cleanedResult = Object.fromEntries(Object.entries(iResult).filter(([_, v]) => v !== undefined)) as IResult;
    return cleanedResult;
  }

  public getStars = (teams: Record<string, string[]>, results: ResultInstance[], players: Record<string, IPlayerStats>): number => {
    if (!this.initialisedStars) {
      this.initialisedStars = true;
      this.difficulty = getFormDifficulty(this.team, teams, results);
      const stats = getRecords(results, players);

      this.isHighestWin = stats?.biggestWin?.results?.some(r => r === formatResult(this)) || false;
      this.isHighestLoss = stats?.biggestLoss?.results?.some(r => r === formatResult(this)) || false;
      this.isMostGoals = stats?.mostGoals?.results?.some(r => r === formatResult(this)) || false;
      this.isCleanSheet = stats?.cleanSheets?.results?.some(r => r === formatResult(this)) || false;
      this.isUnbeatenStreak = stats?.unbeatenStreak?.results?.some(r => r === formatResult(this)) || false;
      this.isMostGoalsInGame = stats?.mostGoalsInGame?.results?.some(r => r === formatResult(this)) || false;

      let stars = this.result === 'W' ? 1 : this.result === 'D' ? 0 : -1; // Default to 0 stars for a win, -1 for a loss
      if (this.difficulty < 0.75) stars += 1;
      else if (this.difficulty > 1.5) stars += 3;
      else stars += 2;
      if (this.isHighestLoss) stars--;
      if (this.isHighestWin) stars++;
      if (this.isMostGoals) stars++;
      if (this.isMostGoalsInGame) stars++;
      if (this.isCleanSheet) stars++;
      if (this.isUnbeatenStreak) stars++;
      if (stars < 0) stars = 0;
      this.stars = stars;
    }
    return this.stars;
  }

  initialisedStars: boolean = false;
  difficulty: number;
  isHighestWin: boolean = false;
  isHighestLoss: boolean = false;
  isMostGoals: boolean = false;
  isMostGoalsInGame: boolean = false;
  isCleanSheet: boolean = false;
  isUnbeatenStreak: boolean = false;
  stars: number = 0;
  date: string;
  team: string;
  venue: string;
  score: string;
  result: string;
  report: string;
  videoLink?: string;
  scorers: string[];
  assists: string[];
  played?: string[];
  cleanSheet?: string[];
  goalKeepers?: string[];
  defenders?: string[];
  midfielders?: string[];
  attackers?: string[];
  private _comments?: Record<string, string>;
  private _likes?: string[];
  links?: string[];
  otherLinks?: string[];
  matchType?: MatchType; // e.g., "league", "friendly", "cup"
  matchFormat?: MatchFormat; // e.g., "5-a-side", "7-a-side", "11-a-side"
  surface?: PitchSurface; // e.g., "Grass", "3G/Astro", "Indoor"
  weather?: WeatherCondition[];
  formation?: string;
  captain?: string;
  playerOfTheMatch?: string;
  otherAwards?: Record<string, AwardWinners>;
  division?: string;
}

export type MatchType = 'League' | 'Friendly' | 'Cup';
export type MatchFormat = '5v5' | '6v6' | '7v7' | '9v9' | '11v11';
export type PitchSurface = 'Grass' | '3G/Astro' | 'Indoor';
export type WeatherCondition = 'Sunny' | 'Cloudy' | 'Wet' | 'Rain' | 'Cold' | 'Hot' | 'Windy' | 'Snow' | 'Fog' | 'Humid';

export interface ITicket {
  id: string;
  subject: string;
  description: string;
  priority?: 'low' | 'medium' | 'high';
  // assigned_to?: string; // User ID of the person assigned to the ticket
  displayName: string; // Name of the person who created the ticket
  uid: string; // User ID of the person who created the ticket
  email: string; // User ID of the person who reported the ticket
  status?: 'open' | 'in progress' | 'closed';
  created_at: string;
  updated_at?: string;
  comments?: Record<string, string>; // Comments on the ticket, keyed by user ID
  selectedTeam?: string; // Team ID of the team this ticket is related to
}


export interface INotification {
  action: string; // Action type (e.g., "goal", "assist", "substitution")
  user: string; // User ID of the person who performed the action
  details: string; // Details of the action (e.g., "Player X scored a goal")
  timestamp: string; // Timestamp of the action
  teamName?: string; // Optional team name if the action is team-specific
}

export interface ITrafficLog extends INotification {
  eventName: string;
  page?: string;
  platform?: string;
  campaignSource?: string;
  campaignMedium?: string;
  campaignName?: string;
  campaignContent?: string;
}

export interface INewsArticle {
  id: string;
  title: string;
  content: string;
  date: string;
  link?: string;
}

export interface IChatMessage {
  id: string;
  text: string;
  authorUid: string;
  authorName: string;
  timestamp: number;
  deleted?: boolean;
  deletedBy?: string;
  /** Parent message id for replies; undefined/null means top-level */
  threadId?: string | null;
  /** emoji key → array of user UIDs who reacted */
  reactions?: Record<string, string[]>;
}

// ---- Tactical Videos ----
export interface IVideoAnnotation {
  id: string;
  type: 'arrow' | 'circle' | 'player' | 'hatchCircle' | 'hatchRect';
  color: string;
  timeSeconds: number;           // video time (seconds) when annotation appears
  durationSeconds: 3 | 5 | 10;  // how long it stays visible
  // Arrow — normalised 0-1 relative to video frame width/height
  x1?: number; y1?: number;
  x2?: number; y2?: number;
  // Circle / player / hatchCircle — normalised centre
  x?: number; y?: number;
  // Circle / hatchCircle radius, normalised to frame width
  r?: number;
  // Player badge label
  label?: string;
  // Line style
  dashed?: boolean;
}

export interface ITacticalVideo {
  id: string;
  title: string;
  description?: string;
  storagePath: string;          // Firebase Storage path
  downloadUrl: string;          // HTTP URL for playback
  thumbnailUrl?: string;        // Thumbnail image URL
  durationSeconds?: number;     // Video duration in seconds
  tags?: string[];              // Searchable tags
  playerMentions?: string[];    // Mentioned player names for filtering
  viewCount?: number;           // Incremented when a viewer watches the clip
  attachedBoardIds?: string[];  // Tactics board IDs linked to this video
  annotations?: IVideoAnnotation[];  // Time-stamped drawing overlays
  uploadedAt: string;           // ISO date
  uploadedBy: string;           // uid
  uploadedByName: string;
  fileSizeBytes?: number;
}

export interface ITacticsShapeData {
  id: string;
  type: 'arrow' | 'curvedArrow' | 'circle' | 'rect' | 'player';
  color: string;
  // Arrow / curved arrow
  x1?: number; y1?: number;
  x2?: number; y2?: number;
  cx?: number; cy?: number; // bezier control point for curvedArrow
  // Circle (center + radius)
  x?: number; y?: number; r?: number;
  // Rect (top-left + size)
  w?: number; h?: number;
  // Player marker label
  label?: string;
  // Line style
  dashed?: boolean;
}

export interface ITacticsBoard {
  id: string;
  name: string;
  shapes: ITacticsShapeData[];
  createdAt: string;       // ISO date
  updatedAt: string;       // ISO date
  createdBy: string;       // uid
  createdByName: string;
}